import { ClearOutlined } from '@ant-design/icons';
import { Collapse, Flex, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';
import { JsonView } from '~/app/components/common/JsonView/JsonView';
import { formatConsoleTimestamp } from '../utils';
import type { AssetCommandConsoleEntry } from '../types';
import { useOcppCommandConsoleScroll } from './hooks/useOcppCommandConsoleScroll';
import type { OcppCommandConsoleProps } from './types';
import {
  consoleDirectionLabel,
  consoleEntryTone,
  consoleToneColor,
  shouldCollapsePayload,
} from './utils';

export function OcppCommandConsole({
  entries,
  onClear,
  height = 'min(500px, 60vh)',
}: OcppCommandConsoleProps) {
  const { t } = useTranslation('assets');
  const scrollRef = useOcppCommandConsoleScroll(entries.length);
  const clearLabel = t('detail.commands.console.clear');
  const hasEntries = entries.length > 0;

  return (
    <Flex
      vertical
      gap="small"
      role="region"
      aria-label={t('detail.commands.console.title')}
      style={{
        height,
        width: '100%',
        border: '1px solid #262626',
        borderRadius: 8,
        background: '#0a0a0a',
        padding: 12,
        minHeight: 200,
        boxSizing: 'border-box',
      }}
    >
      <Flex justify="space-between" align="center" style={{ flexShrink: 0 }}>
        <Typography.Text style={{ color: '#e5e5e5', fontWeight: 600 }}>
          {t('detail.commands.console.title')}
        </Typography.Text>
        <Tooltip title={clearLabel}>
          <span>
            <Button
              type="text"
              size="small"
              disabled={!hasEntries}
              onClick={onClear}
              aria-label={clearLabel}
              icon={<ClearOutlined style={{ fontSize: 16 }} />}
              style={{
                color: hasEntries ? '#ff4d4f' : '#525252',
                width: 34,
                height: 34,
                padding: 0,
                justifyContent: 'center',
              }}
            />
          </span>
        </Tooltip>
      </Flex>

      <div
        ref={scrollRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 12,
          lineHeight: 1.55,
          background: '#111111',
          border: '1px solid #262626',
          borderRadius: 6,
          padding: 12,
        }}
      >
        {entries.length === 0 ? (
          <Typography.Text style={{ color: '#737373' }}>
            {t('detail.commands.console.empty')}
          </Typography.Text>
        ) : (
          <Flex vertical gap="middle">
            {entries.map((entry) => (
              <ConsoleEntryRow key={entry.id} entry={entry} />
            ))}
          </Flex>
        )}
      </div>
    </Flex>
  );
}

function ConsoleEntryRow({ entry }: { entry: AssetCommandConsoleEntry }) {
  const { t } = useTranslation('assets');
  const tone = consoleEntryTone(entry);
  const direction = consoleDirectionLabel(entry, t);
  const collapse = shouldCollapsePayload(entry.payload);
  const color = consoleToneColor(tone);
  const message = t(entry.messageKey, entry.messageParams);

  return (
    <div
      style={{
        color,
        borderLeft: `2px solid ${color}`,
        paddingLeft: 10,
      }}
    >
      <Flex gap="small" wrap="wrap" align="baseline">
        <span style={{ color: '#737373', fontFamily: 'inherit' }}>
          {formatConsoleTimestamp(entry.at)}
        </span>
        {direction ? (
          <span style={{ fontWeight: 700, fontFamily: 'inherit', color }}>
            {direction}
          </span>
        ) : null}
        <span style={{ fontFamily: 'inherit', color }}>{message}</span>
        {entry.durationMs != null ? (
          <span style={{ color: '#737373', fontFamily: 'inherit' }}>
            {entry.durationMs} ms
          </span>
        ) : null}
      </Flex>

      {entry.action ? (
        <div style={{ color: '#a3a3a3', fontFamily: 'inherit' }}>
          action: {entry.action}
        </div>
      ) : null}

      {entry.uniqueId ? (
        <div style={{ color: '#a3a3a3', fontFamily: 'inherit' }}>
          uniqueId: {entry.uniqueId}
        </div>
      ) : null}

      {entry.payload ? (
        collapse ? (
          <Collapse
            size="small"
            ghost
            style={{ marginTop: 8 }}
            items={[
              {
                key: 'payload',
                label: (
                  <span style={{ color: '#a3a3a3', fontFamily: 'inherit' }}>
                    {t('detail.commands.console.payload')}
                  </span>
                ),
                children: (
                  <JsonView
                    value={entry.payload}
                    editable={false}
                    wrapInCard={false}
                    theme="monokai"
                    collapsed={1}
                    maxHeight={200}
                    enableClipboard={false}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                    }}
                  />
                ),
              },
            ]}
          />
        ) : (
          <div style={{ marginTop: 8 }}>
            <JsonView
              value={entry.payload}
              editable={false}
              wrapInCard={false}
              theme="monokai"
              collapsed={false}
              maxHeight={200}
              enableClipboard={false}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
              }}
            />
          </div>
        )
      ) : null}
    </div>
  );
}
